import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SortingState, ColumnFiltersState, OnChangeFn } from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuthStore } from "@/app/store/authStore";
import { PaginationState } from "@tanstack/react-table";
import { PaginatedResponse, ListQueryParams } from "@/app/types/api";
import { PriceList, PriceListCreate, PriceListUpdate } from "@/app/types/pricing";
import { listPriceLists, createPriceList, updatePriceList, deletePriceList } from "@/app/services/pricingService";
import PriceListDataTable from "./components/PriceListDataTable";
import PriceListForm from "./components/PriceListForm";
import { columnsPriceList } from "./components/columnsPriceList";
import PriceListItemManager from "./components/PriceListItemManager";

// Define selectors for auth store
const selectUser = (state: { user: any }): any => state.user;
const selectLogoutAction = (state: { logoutAction: () => void }): () => void => state.logoutAction;
const selectIsLoading = (state: { isLoading: boolean }): boolean => state.isLoading;

const PricingPage: React.FC = () => {
  const queryClient = useQueryClient();

  // Local State
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // State for managing the item list view/dialog
  const [isItemManagerOpen, setIsItemManagerOpen] = useState(false);
  const [currentPriceList, setCurrentPriceList] = useState<PriceList | null>(null); // For edit/delete/view items
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    pageCount: 0,
  });

  // Auth state
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);
  const isOrgAdmin = useMemo(() => user?.role?.name === 'org_admin', [user]);

  // Query Params
  const queryParams = useMemo<ListQueryParams>(() => ({
    skip: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
    search_query: searchValue || undefined,
    ...(sorting.length > 0 && { sort_by: sorting[0].id, sort_dir: sorting[0].desc ? 'desc' : 'asc' }),
    ...(columnFilters.length > 0 && { filters: JSON.stringify(columnFilters.reduce((acc, f) => ({ ...acc, [f.id]: f.value }), {})) }),
  }), [pagination.pageIndex, pagination.pageSize, searchValue, sorting, columnFilters]);

  // Fetch Price Lists Query
  const {
    data: priceListsResponse = { items: [], total: 0 },
    isLoading: isPriceListsLoading,
    isError: isPriceListsError,
    error: priceListsError,
    refetch: refetchPriceLists,
  } = useQuery<PaginatedResponse<PriceList>>({
    queryKey: ['priceLists', userOrgId, queryParams],
    queryFn: () => listPriceLists(queryParams), // Backend filters by org implicitly
    enabled: !!userOrgId,
  });

  // Update page count
  useEffect(() => {
    if (priceListsResponse) {
      setPagination(prev => ({ ...prev, pageCount: Math.ceil(priceListsResponse.total / prev.pageSize) }));
    }
  }, [priceListsResponse, pagination.pageSize]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: PriceListCreate) => createPriceList(data),
    onSuccess: () => {
      toast.success('Price list created');
      queryClient.invalidateQueries({ queryKey: ['priceLists', userOrgId] });
      setIsCreateFormOpen(false);
    },
    onError: (error: any) => toast.error(`Creation failed: ${error.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PriceListUpdate }) => updatePriceList(id, data),
    onSuccess: () => {
      toast.success('Price list updated');
      queryClient.invalidateQueries({ queryKey: ['priceLists', userOrgId] });
      setIsEditFormOpen(false);
      setCurrentPriceList(null);
    },
    onError: (error: any) => toast.error(`Update failed: ${error.message}`),
  });

   const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePriceList(id),
    onSuccess: () => {
      toast.success('Price list deleted');
      queryClient.invalidateQueries({ queryKey: ['priceLists', userOrgId] });
      setIsDeleteDialogOpen(false);
      setCurrentPriceList(null);
    },
    onError: (error: any) => toast.error(`Deletion failed: ${error.message}`),
  });

  // Event Listeners from Data Table
  useEffect(() => {
    const handleEdit = (event: CustomEvent<PriceList>) => {
      setCurrentPriceList(event.detail);
      setIsEditFormOpen(true);
    };
    const handleDelete = (event: CustomEvent<PriceList>) => {
      setCurrentPriceList(event.detail);
      setIsDeleteDialogOpen(true);
    };
     const handleViewItems = (event: CustomEvent<PriceList>) => {
      setCurrentPriceList(event.detail);
      setIsItemManagerOpen(true);
    };

    document.addEventListener('edit-pricelist', handleEdit as EventListener);
    document.addEventListener('delete-pricelist', handleDelete as EventListener);
    document.addEventListener('view-pricelist-items', handleViewItems as EventListener);

    return () => {
      document.removeEventListener('edit-pricelist', handleEdit as EventListener);
      document.removeEventListener('delete-pricelist', handleDelete as EventListener);
      document.removeEventListener('view-pricelist-items', handleViewItems as EventListener);
    };
  }, []);

  // Handlers
  const handleCreateClick = () => setIsCreateFormOpen(true);
  const handleCreateSubmit = (data: PriceListCreate) => createMutation.mutate(data);
  const handleEditSubmit = (data: PriceListUpdate) => {
    if (currentPriceList) updateMutation.mutate({ id: currentPriceList.id, data });
  };
  const handleDeleteConfirm = () => {
    if (currentPriceList) deleteMutation.mutate(currentPriceList.id);
  };
  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
     setPagination(prev => {
        const newState = typeof updater === 'function' ? updater(prev) : updater;
        return { ...newState, pageCount: prev.pageCount };
     });
  };
 const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(typeof updater === 'function' ? updater(sorting) : updater);
 };


  // Render Logic
  if (isAuthLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!userOrgId) return <div>Error: User organization not found.</div>; // Or redirect
  if (isPriceListsError) return <div>Error loading price lists: {priceListsError instanceof Error ? priceListsError.message : 'Unknown error'}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Pricing</h1>
        {isOrgAdmin && (
          <Button onClick={handleCreateClick}>
            <Plus className="mr-2 h-4 w-4" /> Add Price List
          </Button>
        )}
      </div>
       <p className="text-muted-foreground">
        Manage price lists for different customer segments or scenarios.
      </p>

       {/* Price Lists Data Table */}
        <PriceListDataTable
            data={priceListsResponse}
            columns={columnsPriceList}
            isLoading={isPriceListsLoading}
            pagination={pagination}
            sorting={sorting}
            columnFilters={columnFilters}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onPaginationChange={handlePaginationChange}
            onSortingChange={handleSortingChange}
            onColumnFiltersChange={setColumnFilters}
        />

      {/* Create Form */}
      <PriceListForm
        open={isCreateFormOpen}
        onOpenChange={setIsCreateFormOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        mode="create"
      />

      {/* Edit Form */}
      {currentPriceList && (
        <PriceListForm
          open={isEditFormOpen}
          onOpenChange={(open) => {
            setIsEditFormOpen(open);
            if (!open) setCurrentPriceList(null);
          }}
          onSubmit={handleEditSubmit}
          defaultValues={currentPriceList}
          isLoading={updateMutation.isPending}
          mode="edit"
        />
      )}

      {/* PriceListItemManager Integration */}
      {currentPriceList && (
        <PriceListItemManager
          priceListId={currentPriceList.id}
          priceListName={currentPriceList.name}
          open={isItemManagerOpen}
          onOpenChange={(open) => {
            setIsItemManagerOpen(open);
            if (!open) setCurrentPriceList(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will permanently delete the price list &quot;{currentPriceList?.name}&quot; and all its item pricing rules. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PricingPage; 